/**
 * KI generiert kann noch überarbeitet werden.
 */
import { isObject } from './reactive-store.helper';

export interface HydratableConstructor {
  readonly _tag: string;
  fromJSON(data: Record<string, unknown>): object;
  prototype: object;
}

const registry = new Map<string, HydratableConstructor>();

/**
 * Base class for objects that survive IDB round-trips.
 *
 * Subclasses must define `static _tag` (a stable string tag)
 * and call `static { this.register(); }` to enter the global registry.
 *
 * - `toJSON()` is called by `dehydrate` during serialization (stores `_tag` tag).
 * - `fromJSON()` is called by `hydrate` during rehydration (reconstructs instance).
 */
export abstract class Hydratable {
  static readonly _tag: string;

  static register(): void {
    registry.set(this._tag, this as unknown as HydratableConstructor);
  }

  static resolve(tag: string): HydratableConstructor | undefined {
    return registry.get(tag);
  }

  /** Override for custom deserialization. Receives already-hydrated children. */
  static fromJSON(data: Record<string, unknown>): object {
    return Object.assign(Object.create(this.prototype), data);
  }

  /** Produces a tagged plain-object snapshot. Override for custom serialization. */
  toJSON(): Record<string, unknown> {
    return {
      _tag: (this.constructor as typeof Hydratable)._tag,
      ...Object.fromEntries(Object.entries(this)),
    };
  }
}

/**
 * Converts an object tree into a plain, IDB-safe representation.
 *
 * - Calls `toJSON()` on Hydratable instances (writes `_tag` tag).
 * - Strips proxies (reads go through proxy get traps, writes go into fresh plain objects).
 * - Preserves shared references.
 */
export function dehydrate<T>(value: T): T {
  const seen = new WeakMap<object, object>();

  function deep(v: unknown): unknown {
    if (!isObject(v)) return v;
    if (seen.has(v)) return seen.get(v);

    if (Array.isArray(v)) {
      const arr: unknown[] = [];
      seen.set(v, arr);
      for (let i = 0; i < v.length; i++) arr[i] = deep(v[i]);
      return arr;
    }

    // Hydratable instances: toJSON() produces a tagged plain snapshot
    if ('toJSON' in v && typeof (v as { toJSON: unknown }).toJSON === 'function') {
      const json = (v as { toJSON(): Record<string, unknown> }).toJSON();
      // Ensure _tag is present even if toJSON() forgot it
      if (!json._tag) {
        const tag = (v.constructor as typeof Hydratable)._tag;
        if (tag) json._tag = tag;
      }
      seen.set(v, json);
      for (const key of Object.keys(json)) {
        json[key] = deep(json[key]);
      }
      return json;
    }

    // Plain objects: shallow copy with recursion
    const plain: Record<string, unknown> = {};
    seen.set(v, plain);
    for (const key of Object.keys(v)) {
      plain[key] = deep((v as Record<string, unknown>)[key]);
    }
    return plain;
  }

  return deep(value) as T;
}

/**
 * Rehydrates a plain object tree (e.g. from IDB) back into class instances.
 *
 * children are hydrated before `fromJSON` is called on the parent.
 * shared references are preserved
 */
export function hydrate<T>(data: unknown): T {
  const seen = new WeakMap<object, object>();

  function deep(v: unknown): unknown {
    if (!isObject(v)) return v;
    if (seen.has(v)) return seen.get(v);

    if (Array.isArray(v)) {
      const arr: unknown[] = [];
      seen.set(v, arr);
      for (let i = 0; i < v.length; i++) arr[i] = deep(v[i]);
      return arr;
    }

    const raw = v as Record<string, unknown>;
    const tag = raw._tag as string | undefined;
    const cls = tag ? Hydratable.resolve(tag) : undefined;

    // Pre-register a shell in seen BEFORE recursing,
    // so circular refs resolve to the same instance.
    const shell = (cls ? Object.create(cls.prototype) : {}) as Record<string, unknown>;
    seen.set(v, shell);

    // Hydrate children
    const childData: Record<string, unknown> = {};
    for (const key of Object.keys(raw)) {
      if (key === '_tag') continue;
      childData[key] = deep(raw[key]);
    }

    // Reconstruct: call fromJSON for tagged objects, plain assign otherwise
    if (cls) {
      Object.assign(shell, cls.fromJSON(childData));
    } else {
      Object.assign(shell, childData);
    }

    return shell;
  }

  return deep(data) as T;
}
